"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

type VariantKey = "primary" | "secondary" | "destructive";
type AppearanceKey = "solid" | "outline" | "ghost" | "gradient" | "link";
type ToneKey = "light" | "base" | "dark";
type ShapeKey = "square" | "circle" | "rounded";

const shapeStyles: Record<ShapeKey, string> = {
  square: "aspect-square p-0",
  circle: "rounded-full p-0 aspect-square",
  rounded: "rounded-md",
};

const appearanceToneStyles: Record<AppearanceKey, Record<ToneKey, string>> = {
  solid: {
    light:
      "bg-[var(--btn-bg)] text-[var(--btn-text)] hover:bg-[var(--btn-hover)] disabled:bg-[var(--btn-disabled)]",
    base: "bg-[var(--btn-bg)] text-[var(--btn-text)] hover:bg-[var(--btn-hover)] disabled:bg-[var(--btn-disabled)]",
    dark: "bg-[var(--btn-bg)] text-[var(--btn-text)] hover:bg-[var(--btn-hover)] disabled:bg-[var(--btn-disabled)]",
  },
  outline: {
    light:
      "border text-[var(--btn-text)] border-[var(--btn-border)] hover:bg-[var(--btn-hover)] disabled:border-[var(--btn-border-disabled)] disabled:text-[var(--btn-text-disabled)]",
    base: "border text-[var(--btn-bg)] border-[var(--btn-border)] bg-transparent hover:bg-[var(--btn-hover)] disabled:border-[var(--btn-border-disabled)] disabled:text-[var(--btn-text-disabled)]",
    dark: "border text-[var(--btn-bg)] border-[var(--btn-border)] hover:bg-[var(--btn-hover)] disabled:border-[var(--btn-border-disabled)] disabled:text-[var(--btn-text-disabled)]",
  },
  ghost: {
    light:
      "text-[var(--btn-text)] bg-[var(--btn-bg)] hover:bg-[var(--btn-hover)] disabled:text-[var(--btn-text-disabled)]",
    base: "text-[var(--btn-text)] bg-[var(--btn-bg)] hover:bg-[var(--btn-hover)] disabled:text-[var(--btn-text-disabled)]",
    dark: "text-[var(--btn-text)] bg-[var(--btn-bg)] hover:bg-[var(--btn-hover)] disabled:text-[var(--btn-text-disabled)]",
  },
  gradient: {
    light:
      "bg-gradient-to-b from-[var(--btn-from)] to-[var(--btn-to)] text-[var(--btn-text)] hover:opacity-90 disabled:opacity-50",
    base: "bg-gradient-to-b from-[var(--btn-from)] to-[var(--btn-to)] text-[var(--btn-text)] hover:opacity-90 disabled:opacity-50",
    dark: "bg-gradient-to-b from-[var(--btn-from)] to-[var(--btn-to)] text-[var(--btn-text)] hover:opacity-90 disabled:opacity-50",
  },
  link: {
    light: "underline-offset-4 text-[var(--btn-text)] hover:underline",
    base: "underline-offset-4 text-[var(--btn-text)] hover:underline",
    dark: "underline-offset-4 text-[var(--btn-text)] hover:underline",
  },
};
type CSSVars = Record<string, string>;
const colorMap: Record<VariantKey, Record<ToneKey, CSSVars>> = {
  primary: {
    light: {
      "--btn-bg": "var(--color-primary-200)",
      "--btn-text": "var(--color-primary-500)",
      "--btn-hover": "var(--color-primary-300)",
      "--btn-disabled": "var(--color-primary-50)",
      "--btn-border": "var(--color-primary-100)",
      "--btn-border-disabled": "var(--color-primary-50)",
      "--btn-text-disabled": "var(--color-primary-200)",
      "--btn-from": "var(--color-primary-100)",
      "--btn-to": "var(--color-primary-300)",
    },
    base: {
      "--btn-bg": "var(--color-primary-500)",
      "--btn-text": "white",
      "--btn-hover": "var(--color-primary-700)",
      "--btn-disabled": "var(--color-primary-200)",
      "--btn-border": "var(--color-primary-500)",
      "--btn-border-disabled": "var(--color-primary-200)",
      "--btn-text-disabled": "var(--color-primary-200)",
      "--btn-from": "var(--color-primary-400)",
      "--btn-to": "var(--color-primary-700)",
    },
    dark: {
      "--btn-bg": "var(--color-primary-700)",
      "--btn-text": "white",
      "--btn-hover": "var(--color-primary-900)",
      "--btn-disabled": "var(--color-primary-300)",
      "--btn-border": "var(--color-primary-800)",
      "--btn-border-disabled": "var(--color-primary-300)",
      "--btn-text-disabled": "var(--color-primary-300)",
      "--btn-from": "var(--color-primary-600)",
      "--btn-to": "var(--color-primary-700)",
    },
  },
  secondary: {
    light: {
      "--btn-bg": "var(--color-gray-200)",
      "--btn-text": "var(--color-gray-700)",
      "--btn-hover": "var(--color-gray-300)",
      "--btn-disabled": "var(--color-gray-50)",
      "--btn-border": "var(--color-gray-300)",
      "--btn-border-disabled": "var(--color-gray-50)",
      "--btn-text-disabled": "var(--color-gray-200)",
      "--btn-from": "var(--color-gray-100)",
      "--btn-to": "var(--color-gray-300)",
    },
    base: {
      "--btn-bg": "var(--color-gray-500)",
      "--btn-text": "white",
      "--btn-hover": "var(--color-gray-700)",
      "--btn-disabled": "var(--color-gray-200)",
      "--btn-border": "var(--color-gray-500)",
      "--btn-border-disabled": "var(--color-gray-200)",
      "--btn-text-disabled": "var(--color-gray-200)",
      "--btn-from": "var(--color-gray-400)",
      "--btn-to": "var(--color-gray-700)",
    },
    dark: {
      "--btn-bg": "var(--color-gray-700)",
      "--btn-text": "white",
      "--btn-hover": "var(--color-gray-900)",
      "--btn-disabled": "var(--color-gray-300)",
      "--btn-border": "var(--color-gray-800)",
      "--btn-border-disabled": "var(--color-gray-300)",
      "--btn-text-disabled": "var(--color-gray-300)",
      "--btn-from": "var(--color-gray-600)",
      "--btn-to": "var(--color-gray-700)",
    },
  },
  destructive: {
    light: {
      "--btn-bg": "var(--color-red-200)",
      "--btn-text": "var(--color-red-700)",
      "--btn-hover": "var(--color-red-300)",
      "--btn-disabled": "var(--color-red-50)",
      "--btn-border": "var(--color-red-200)",
      "--btn-border-disabled": "var(--color-red-50)",
      "--btn-text-disabled": "var(--color-red-200)",
      "--btn-from": "var(--color-red-200)",
      "--btn-to": "var(--color-red-300)",
    },
    base: {
      "--btn-bg": "var(--color-red-300)",
      "--btn-text": "white",
      "--btn-hover": "var(--color-red-700)",
      "--btn-disabled": "var(--color-red-200)",
      "--btn-border": "var(--color-red-500)",
      "--btn-border-disabled": "var(--color-red-200)",
      "--btn-text-disabled": "var(--color-red-200)",
      "--btn-from": "var(--color-red-500)",
      "--btn-to": "var(--color-red-600)",
    },
    dark: {
      "--btn-bg": "var(--color-red-700)",
      "--btn-text": "white",
      "--btn-hover": "var(--color-red-900)",
      "--btn-disabled": "var(--color-red-300)",
      "--btn-border": "var(--color-red-800)",
      "--btn-border-disabled": "var(--color-red-300)",
      "--btn-text-disabled": "var(--color-red-300)",
      "--btn-from": "var(--color-red-700)",
      "--btn-to": "var(--color-red-800)",
    },
  },
};

const generateCompoundVariants = () => {
  const variants: {
    variant: VariantKey;
    appearance: AppearanceKey;
    tone: ToneKey;
    shape: ShapeKey;
    class: string;
  }[] = [];

  for (const variant of [
    "primary",
    "secondary",
    "destructive",
  ] as VariantKey[]) {
    for (const appearance of Object.keys(
      appearanceToneStyles,
    ) as AppearanceKey[]) {
      for (const tone of ["light", "base", "dark"] as ToneKey[]) {
        for (const shape of Object.keys(shapeStyles) as ShapeKey[]) {
          const toneClass = appearanceToneStyles[appearance][tone];
          const shapeClass = shapeStyles[shape];
          variants.push({
            variant,
            appearance,
            tone,
            shape,
            class: cn(toneClass, shapeClass),
          });
        }
      }
    }
  }
  return variants;
};

const buttonVariants = cva(
  "inline-flex items-center justify-center font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 cursor-pointer disabled:pointer-events-none gap-1",
  {
    variants: {
      variant: {
        primary: "",
        secondary: "",
        destructive: "",
      },
      appearance: {
        solid: "",
        outline: "",
        gradient: "",
        ghost: "",
        link: "",
      },
      size: {
        "2xs": "py-2 px-3 text-2xs ",
        xs: "py-2 px-3 text-sm leading-140 font-medium",
        sm: "py-2 px-3 text-base leading-140 font-medium",
        md: "py-3 px-4 text-sm leading-130 font-bold",
        lg: "py-4 px-5 text-sm leading-130 font-bold;",
        xl: "py-5 px-6 text-xl leading-130 font-semibold",
      },
      shape: shapeStyles,
      tone: {
        light: "",
        base: "",
        dark: "",
      },
    },
    compoundVariants: generateCompoundVariants(),
    defaultVariants: {
      variant: "primary",
      appearance: "solid",
      size: "md",
      shape: "rounded",
      tone: "base",
    },
  },
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "prefix">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  classPrefix?: string;
  classSuffix?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      classPrefix,
      classSuffix,
      variant = "primary",
      appearance = "solid",
      size = "md",
      shape = "rounded",
      tone = "base",
      prefix,
      suffix,
      asChild,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const styleVars = colorMap[variant ?? "primary"]?.[tone ?? "base"] ?? {};

    const innerContent = (
      <>
        {prefix && (
          <span className={cn("mr-1 flex items-center", classPrefix)}>
            {prefix}
          </span>
        )}
        {children}
        {suffix && (
          <span className={cn("ml-1 flex items-center", classSuffix)}>
            {suffix}
          </span>
        )}
      </>
    );
    if (asChild && !prefix && !suffix) {
      return (
        <Comp
          ref={ref}
          style={styleVars}
          className={cn(
            buttonVariants({ variant, appearance, size, shape, tone }),
            className,
          )}
          {...props}
        >
          {children}
        </Comp>
      );
    }
    // When using asChild (Radix Slot), Slot requires a single React element child.
    // Wrap the inner content into a single <span> so Slot receives exactly one child
    // while still allowing the Slot to forward props/className/style to that element.
    const childToRender = asChild ? <span>{innerContent}</span> : innerContent;

    return (
      <Comp
        ref={ref}
        style={styleVars}
        className={cn(
          buttonVariants({ variant, appearance, size, shape, tone }),
          className,
        )}
        {...props}
      >
        {childToRender}
      </Comp>
    );
  },
);

Button.displayName = "Button";
export { Button, buttonVariants };
